import { useRef, type KeyboardEvent } from 'react';
import styles from './Tabs.module.css';

export interface TabDef<Id extends string> {
  id: Id;
  label: string;
}

interface Props<Id extends string> {
  tabs: TabDef<Id>[];
  active: Id;
  onChange: (id: Id) => void;
  ariaLabel?: string;
  className?: string;
}

export function Tabs<Id extends string>({ tabs, active, onChange, ariaLabel = 'Result sections', className }: Props<Id>) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const idx = tabs.findIndex((t) => t.id === active);
    if (idx < 0) return;
    let next = idx;
    if (e.key === 'ArrowRight') next = (idx + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else return;
    e.preventDefault();
    const target = tabs[next]!;
    onChange(target.id);
    refs.current[target.id]?.focus();
  };

  return (
    <div role="tablist" aria-label={ariaLabel} className={[styles.bar, className].filter(Boolean).join(' ')}>
      {tabs.map((t) => {
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            ref={(el) => { refs.current[t.id] = el; }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${t.id}`}
            id={`tab-${t.id}`}
            tabIndex={isActive ? 0 : -1}
            className={[styles.tab, isActive ? styles.active : ''].join(' ')}
            onClick={() => onChange(t.id)}
            onKeyDown={onKey}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
